import { createContext, forwardRef } from 'react';
import {
  CheckboxGroup as AriaCheckboxGroup,
  type CheckboxGroupProps as AriaCheckboxGroupProps,
  composeRenderProps,
  Provider,
} from 'react-aria-components';

import type { BaseStyleProps, UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useStyleProps } from '@infinex/ui-styles';

import { FieldContext, FieldLabel, HelpText } from '../field';

// Context
// -----------------------------------------------------------------------------

export type CheckboxGroupContextValue = {
  /**
   * The visual prominence of the checkbox.
   * @default default
   */
  prominence?: 'default' | 'low';
};

export const CheckboxGroupContext =
  createContext<CheckboxGroupContextValue | null>(null);

// Main
// -----------------------------------------------------------------------------

export type CheckboxGroupProps = {
  /** The label of the field. For "visually hidden" labels, use the `aria-label` attribute. */
  label?: string;
  /**
   * Hint text is displayed below the label to give extra context or instruction
   * about what a user should choose for the value.
   */
  hint?: string;
} & CheckboxGroupContextValue &
  Omit<
    AriaCheckboxGroupProps,
    'orientation' | 'className' | 'style' | 'isInvalid' | 'validationBehavior'
  > &
  BaseStyleProps &
  UnsafeStyleProps;
// ^ RAC omissions:
// - children, className, and style: render props not supported
// - isInvalid, validationBehavior: we don't (yet) support realtime validation

/**
 * A checkbox group allows the user to select one or more items from a list of
 * options.
 *
 * [Taurus docs: `CheckboxGroup`](https://ui-sb.app.infinex.dev/?path=/docs/components-checkboxgroup--docs)
 */
export const CheckboxGroup = forwardRef<HTMLDivElement, CheckboxGroupProps>(
  function CheckboxGroup(props, forwardedRef) {
    const { hint, label, prominence, ...otherProps } = props;
    const { className, style } = useStyleProps(props);

    return (
      <AriaCheckboxGroup
        {...otherProps}
        className={cn('flex w-full flex-col items-start gap-2', className)}
        style={style}
        ref={forwardedRef}
      >
        {composeRenderProps(props.children, (children, renderProps) => (
          <Provider
            values={[
              // The "size" prop is required by `FieldContext`, but it isn't
              // consumed by the `Checkbox` component
              [FieldContext, { size: 'medium', ...renderProps }],
              [CheckboxGroupContext, { prominence }],
            ]}
          >
            {label ? <FieldLabel>{label}</FieldLabel> : null}

            <div className="grid gap-2">{children}</div>

            <HelpText description={hint} />
          </Provider>
        ))}
      </AriaCheckboxGroup>
    );
  }
);
